import React, { useEffect, useContext, useRef, useState } from 'react';
import NotificationsIcon from "@material-ui/icons/Notifications";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import Badge from "@material-ui/core/Badge";
import Tooltip from "@material-ui/core/Tooltip";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Box from "@material-ui/core/Box";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import { AppBar } from '@material-ui/core';
import { getNotifications } from '../apis';
import { BsCodeSlash } from "react-icons/bs";
import { SiTestcafe } from "react-icons/si";
import ExitToApp from "@material-ui/icons/ExitToApp";
import LockIcon from "@material-ui/icons/Lock";
import { AuthContext } from "../context/auth-context";
import { useNavigate } from 'react-router-dom';
import SimpleModal from "./Modal";
import Grid from '@material-ui/core/Grid';
import Snackbar from '../components/Snackbar';
import { colors } from "../constants/colors";
import useToken from "../hooks/useToken";
import Launchpad from './Launchpad';
import { ChangePass } from './ChangePassword';
import CCLLogo from '../assets/images/logo.jpeg';
import Button from "./Button";
import { logoutStrings, commonStrings } from "./StringBundle";
import { ToggleBtn } from './ToogleBtn/ToggleBtn';
import { refreshAccessTokenOnce } from '../config/axios';
import { REFRESH_BUFFER_RATIO, DEFAULT_TOKEN_EXPIRES_IN_SECONDS, REFRESH_RETRY_DELAY_MS, REFRESH_TOKEN_EXPIRED_CODE, LAST_ACTIVITY_KEY, ACTIVITY_THROTTLE_MS, TOKEN_REFRESH_DUE_AT_KEY, SESSION_EXPIRED_MESSAGE_KEY, IDLE_LOGOUT_SECONDS } from '../constants/autoLogout';
import './AppBar.css';
import AppSidebar from './AppSidebar';
import { getInstance } from '../constants/sidebarConstants';

// function timeAgo(dateStr) {
//   if (!dateStr) return '';
//   const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
//   if (diff < 60) return 'Just now';
//   if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
//   if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
//   return `${Math.floor(diff / 86400)}d ago`;
// }

const CustomAppBar = (props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem('RowsPerPage') ? Number(localStorage.getItem('RowsPerPage')) : 25
  );
  const [allNotificationData, setAllNotificationData] = useState(null);
  const [countNotification, setCountNotification] = useState(-1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElN, setAnchorElN] = useState(null);
  const [openChangePass, setChangePass] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [showSnack, setSnack] = useState({ open: false, message: '', severity: '' });

  const { getCurrentUserDetails, getPreference } = useToken();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const currentUserDetails = getCurrentUserDetails();

  const warningTimeoutId = useRef(null);
  const logoutTimeoutId = useRef(null);
  const countdownInterval = useRef(null);
  const refreshTimerRef = useRef(null);
  const lastRefreshAttemptRef = useRef(Date.now());
  const lastScheduledRefreshDelayMsRef = useRef(DEFAULT_TOKEN_EXPIRES_IN_SECONDS * 1000 * REFRESH_BUFFER_RATIO);
  const lastNotifFetchTimeRef = useRef(0);
  const isActivityThrottledRef = useRef(false);
  const activityThrottleTimeoutRef = useRef(null);
  const timerCountLogout = IDLE_LOGOUT_SECONDS; // 30 min idle
  const timerCountDown = 60;
  const timerCountStart = timerCountLogout - timerCountDown;
  const hostname = getInstance();
  const isDevInstance = hostname === commonStrings.development;
  const isSandboxInstance = hostname === commonStrings.qa;

  // Mirrors showWarningModal for the activity listeners below, which are attached once on mount.
  const showWarningModalRef = useRef(showWarningModal);
  useEffect(() => {
    showWarningModalRef.current = showWarningModal;
  }, [showWarningModal]);

  /* ── Auto logout timer ── */
  // Schedules the warning/logout timeouts without writing lastActivity, so the cross-tab resync below can't ping-pong with other tabs.
  const armIdleTimers = () => {
    clearTimeout(warningTimeoutId.current);
    clearTimeout(logoutTimeoutId.current);
    clearInterval(countdownInterval.current);

    warningTimeoutId.current = setTimeout(() => {
      setShowWarningModal(true);
      setCountdown(timerCountDown);
      countdownInterval.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) { clearInterval(countdownInterval.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }, timerCountStart * 1000);

    logoutTimeoutId.current = setTimeout(() => {
      // This timer can fire late if the browser froze/throttled this backgrounded tab —
      // in that case it may have missed the 'storage' event another tab's activity would
      // have used to re-arm it. Re-check the shared timestamp before actually ending the
      // session: a stale local timer must not log out a session that's active elsewhere.
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (Date.now() - lastActivity < timerCountLogout * 1000) {
        syncFromOtherTab();
        return;
      }
      getLogout('autologout');
    }, timerCountLogout * 1000);
  };

  const resetTimer = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    armIdleTimers();
  };

  // Another tab just recorded activity — dismiss this tab's warning and restart its idle clock too.
  const syncFromOtherTab = () => {
    setShowWarningModal(false);
    armIdleTimers();
  };

  const stayLoggedIn = () => {
    setShowWarningModal(false);
    clearInterval(countdownInterval.current);
    setCountdown(timerCountStart);
    resetTimer();
  };

  useEffect(() => {
    const incrementTabs = () => {
      const count = parseInt(localStorage.getItem('openTabs') || '0', 10);
      localStorage.setItem('openTabs', count + 1);
    };
    // Bookkeeping only — fires on any navigation away, not just a real tab close, so it must never re-run logout.
    const decrementTabs = () => {
      const count = parseInt(localStorage.getItem('openTabs') || '0', 10);
      const newCount = Math.max(count - 1, 0);
      localStorage.setItem('openTabs', newCount);
    };
    // Throttled to ACTIVITY_THROTTLE_MS; once the warning modal is up, ambient activity shouldn't silently reset it — only stayLoggedIn should.
    const handleActivity = () => {
      if (showWarningModalRef.current) return;
      if (isActivityThrottledRef.current) return;
      isActivityThrottledRef.current = true;
      activityThrottleTimeoutRef.current = setTimeout(() => {
        isActivityThrottledRef.current = false;
      }, ACTIVITY_THROTTLE_MS);
      resetTimer();
    };
    // Cross-tab: another tab's activity means the user is still around, even if this tab is idle.
    const handleStorageActivity = (e) => {
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        syncFromOtherTab();
      } else if (e.key === TOKEN_REFRESH_DUE_AT_KEY && e.newValue) {
        // Another tab already refreshed and pushed the due time out — adopt it instead of waiting out our own stale timer.
        const dueAt = parseInt(e.newValue, 10);
        if (dueAt > Date.now()) {
          clearTimeout(refreshTimerRef.current);
          lastRefreshAttemptRef.current = Date.now();
          scheduleRefreshAt(dueAt);
        }
      }
    };

    resetTimer();
    incrementTabs();
    window.addEventListener('pagehide', decrementTabs);
    window.addEventListener('beforeunload', decrementTabs);
    window.addEventListener('storage', handleStorageActivity);
    const events = ['mousemove', 'keydown', 'keypress', 'click', 'scroll', 'touchstart', 'touchmove'];
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));

    // Proactive silent refresh while active — reschedules off the server's real expires_in each time, and shares the due time so other tabs skip their own redundant call.
    const scheduleNextRefresh = (expiresInSeconds) => {
      const delayMs = expiresInSeconds * 1000 * REFRESH_BUFFER_RATIO;
      lastScheduledRefreshDelayMsRef.current = delayMs;
      localStorage.setItem(TOKEN_REFRESH_DUE_AT_KEY, (Date.now() + delayMs).toString());
      refreshTimerRef.current = setTimeout(runProactiveRefresh, delayMs);
    };

    // Adopts a due time another tab already established — no localStorage write, so tabs can't ping-pong resetting each other.
    const scheduleRefreshAt = (dueAt) => {
      const delayMs = Math.max(dueAt - Date.now(), 0);
      lastScheduledRefreshDelayMsRef.current = delayMs;
      refreshTimerRef.current = setTimeout(runProactiveRefresh, delayMs);
    };

    const runProactiveRefresh = async () => {
      if (showWarningModalRef.current) {
        // Local-only deferral (this tab is about to warn/logout on its own) — must not overwrite the shared due time other tabs rely on.
        const delayMs = 60 * 1000;
        lastScheduledRefreshDelayMsRef.current = delayMs;
        refreshTimerRef.current = setTimeout(runProactiveRefresh, delayMs);
        return;
      }
      // The access token cookie is shared across tabs — if another tab already refreshed it, skip our own call entirely.
      const sharedDueAt = parseInt(localStorage.getItem(TOKEN_REFRESH_DUE_AT_KEY) || '0', 10);
      if (sharedDueAt > Date.now()) {
        lastRefreshAttemptRef.current = Date.now();
        scheduleRefreshAt(sharedDueAt);
        return;
      }
      try {
        const res = await refreshAccessTokenOnce();
        lastRefreshAttemptRef.current = Date.now();
        scheduleNextRefresh(res.data?.expires_in || DEFAULT_TOKEN_EXPIRES_IN_SECONDS);
      } catch (err) {
        lastRefreshAttemptRef.current = Date.now();
        // Any other failure (REFRESH_FAILED, network error) is transient, so only a confirmed dead refresh token logs out.
        if (err.response?.data?.code === REFRESH_TOKEN_EXPIRED_CODE) {
          getLogout();
          return;
        }
        lastScheduledRefreshDelayMsRef.current = REFRESH_RETRY_DELAY_MS;
        refreshTimerRef.current = setTimeout(runProactiveRefresh, REFRESH_RETRY_DELAY_MS);
      }
    };

    // On mount, adopt another tab's already-scheduled refresh if one is still pending, instead of guessing with the default.
    const initialSharedDueAt = parseInt(localStorage.getItem(TOKEN_REFRESH_DUE_AT_KEY) || '0', 10);
    if (initialSharedDueAt > Date.now()) {
      scheduleRefreshAt(initialSharedDueAt);
    } else {
      scheduleNextRefresh(DEFAULT_TOKEN_EXPIRES_IN_SECONDS);
    }

    // Fetch notification count on mount + poll every 5 min while the tab is visible.
    const NOTIF_POLL_INTERVAL_MS = 300000;
    const fetchCount = async () => {
      if (document.hidden) return;
      const res = await getNotifications({ userid: currentUserDetails.id, get_count: true });
      setCountNotification(parseInt(res.notify_count));
      lastNotifFetchTimeRef.current = Date.now();
    };
    fetchCount();
    const notifInterval = setInterval(fetchCount, NOTIF_POLL_INTERVAL_MS);

    // Backgrounded tabs get timers throttled, so catch up on refocus on whatever's overdue.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAttemptRef.current >= lastScheduledRefreshDelayMsRef.current) {
        clearTimeout(refreshTimerRef.current);
        runProactiveRefresh();
      }
      if (Date.now() - lastNotifFetchTimeRef.current >= NOTIF_POLL_INTERVAL_MS) {
        fetchCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach((e) => document.removeEventListener(e, handleActivity));
      window.removeEventListener('pagehide', decrementTabs);
      window.removeEventListener('beforeunload', decrementTabs);
      window.removeEventListener('storage', handleStorageActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(warningTimeoutId.current);
      clearTimeout(logoutTimeoutId.current);
      clearInterval(countdownInterval.current);
      clearTimeout(refreshTimerRef.current);
      clearTimeout(activityThrottleTimeoutRef.current);
      clearInterval(notifInterval);
    };
  }, []);

  /* ── Auth actions ── */
  // A hard page load, not navigate() — a full reload guarantees any stuck modal/provider state above the route is wiped too.
  const getLogout = async (type = '') => {
    try {
      await auth.removeToken(type === 'autologout' ? 'auto' : 'manual');
      localStorage.clear();
      sessionStorage.clear();
      // Set after clearing so it survives the reload below — Login.tsx reads it to show the "logged out due to inactivity" message.
      if (type === 'autologout') {
        sessionStorage.setItem('autoLogoutMessage', '1');
      }
      window.location.pathname = '/login';
    } catch (e) { console.error('Logout failed:', e); }
  };

  /* ── Notification handlers ── */
  const openNotification = async (event) => {
    setAnchorElN(event.currentTarget);
    const res = await getNotifications({ userid: currentUserDetails.id, view: true });
    setAllNotificationData(res.notifications_feed);
  };

  const handleCloseN = () => setAnchorElN(null);

  const getAllRead = async () => {
    setAllNotificationData([]);
    getNotifications({ userid: currentUserDetails.id, read_all: true });
    setAnchorElN(null);
    const res = await getNotifications({ userid: currentUserDetails.id, get_count: true });
    setCountNotification(parseInt(res.notify_count));
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(+e.target.value); setPage(0); };

  /* ── Profile menu handlers ── */
  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const isSmallScreen = () => window.innerWidth < 960;

  const profileOpen = Boolean(anchorEl);
  const notifOpen = Boolean(anchorElN);
  const notifId = notifOpen ? 'notif-popover' : undefined;
  const profileId = profileOpen ? 'profile-popover' : undefined;

  const visibleNotifs = allNotificationData
    ? allNotificationData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  /* ── Warning modal body ── */
  function warningModalBody() {
    return (
      <Grid container direction="row">
        <Box>
          <Typography variant="h6">
            {logoutStrings.warningMessage}{countdown}{logoutStrings.warningMessageSecond}
          </Typography>
        </Box>
      </Grid>
    );
  }

  return (
    <div className="app-layout">
      {showSnack.open && (
        <Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: '', severity: '' })} />
      )}

      <AppSidebar current_role={currentUserDetails.role} onUserClick={handleClick} />

      <div className="app-main">
        <AppBar position="static" className="ccl-appbar" style={{ backgroundColor: "#f36a33" }}>
          <Toolbar className="ccl-toolbar">
            <div className="ccl-toolbar-inner">

              <div className="ccl-toolbar-center">
                {/* <img className="ccl-logo-img" src={CCLLogo} alt="CCL Logo" /> */}
                <span className="ccl-company-name">CCL Products (India) Limited</span>
              </div>

              {/* ── Right: Actions ── */}
              <div className="ccl-actions">
                {(isDevInstance || isSandboxInstance) && (
                  <Tooltip title={hostname} arrow>
                    <IconButton className="ccl-icon-btn" aria-label="Instance">
                      {isDevInstance
                        ? <BsCodeSlash className="ccl-notif-bell-icon" />
                        : <SiTestcafe className="ccl-notif-bell-icon" />
                      }
                    </IconButton>
                  </Tooltip>
                )}

                <IconButton
                  className="ccl-icon-btn"
                  aria-describedby={notifId}
                  onClick={openNotification}
                  title="Notifications"
                >
                  <Badge
                    badgeContent={countNotification > 0 ? countNotification : null}
                    className="ccl-notif-badge"
                    overlap="rectangular"
                  >
                    {countNotification > 0
                      ? <NotificationsIcon className="ccl-notif-bell-icon" />
                      : <NotificationsNoneIcon className="ccl-notif-bell-icon" />
                    }
                  </Badge>
                </IconButton>

              </div>
            </div>
          </Toolbar>
        </AppBar>

        <Popover
          id={notifId}
          open={notifOpen}
          anchorEl={anchorElN}
          onClose={handleCloseN}
          className="ccl-notif-popover"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: 'ccl-popover-paper' }}
        >
          <div className="ccl-notif-panel">
            {/* Header */}
            <div className="ccl-notif-header">
              <div className="ccl-notif-title">
                <NotificationsIcon className="ccl-notif-panel-icon" />
                Notifications
                {countNotification > 0 && (
                  <span className="ccl-notif-count-badge">{countNotification}</span>
                )}
              </div>
              <button className="ccl-mark-all-btn" onClick={getAllRead}>
                Mark all read
              </button>
            </div>

            {/* Body */}
            <div className="ccl-notif-list">
              {allNotificationData === null ? (
                /* Loading shimmer */
                <div className="ccl-notif-loading">
                    <div  className="ccl-shimmer-row">
                      <div className="ccl-shimmer ccl-shimmer-avatar" />
                      <div className="ccl-shimmer-body">
                        <div className="ccl-shimmer ccl-shimmer-title" />
                        <div className="ccl-shimmer ccl-shimmer-sub" />
                      </div>
                    </div>
                </div>
              ) : visibleNotifs.length === 0 ? (
                <div className="ccl-notif-empty">
                  <NotificationsNoneIcon className="ccl-notif-empty-icon" />
                  <span className="ccl-notif-empty-text">All caught up!</span>
                  <span className="ccl-notif-empty-hint">No new notifications</span>
                </div>
              ) : (
                visibleNotifs?.map((item, index) => (
                  <div key={index} className={`ccl-notif-item ${!item.is_read ? 'unread' : ''}`}>
                    <div className={`ccl-notif-dot ${item.is_read ? 'read' : ''}`} />
                    <div className="ccl-notif-icon">
                      <NotificationsIcon className="ccl-notif-item-icon" />
                    </div>
                    <div className="ccl-notif-body">
                      <p className="ccl-notif-text">{item.status}</p>
                      {/* {item.created_at && (
                        <p className="ccl-notif-time">{timeAgo(item.created_at)}</p>
                      )} */}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination footer */}
            {allNotificationData && allNotificationData.length > 0 && (() => {
              const total = allNotificationData.length;
              const totalPages = Math.ceil(total / rowsPerPage);
              const from = page * rowsPerPage + 1;
              const to = Math.min((page + 1) * rowsPerPage, total);
              return (
                <div className="ccl-notif-footer">
                  <span className="ccl-notif-footer-info">
                    Showing <strong>{from}–{to}</strong> of <strong>{total}</strong>
                  </span>
                  <div className="ccl-notif-footer-nav">
                    <button
                      className="ccl-page-btn"
                      onClick={() => handleChangePage(null, page - 1)}
                      disabled={page === 0}
                      title="Previous page"
                    >
                      <ChevronLeftIcon className="ccl-page-btn-icon" />
                    </button>
                    <span className="ccl-page-indicator">{page + 1} / {totalPages}</span>
                    <button
                      className="ccl-page-btn"
                      onClick={() => handleChangePage(null, page + 1)}
                      disabled={page >= totalPages - 1}
                      title="Next page"
                    >
                      <ChevronRightIcon className="ccl-page-btn-icon" />
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </Popover>

        <Popover
          id={profileId}
          open={profileOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          className="ccl-profile-menu"
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          PaperProps={{ className: 'ccl-popover-paper' }}
        >
          <div className="ccl-profile-menu-content">
            {/* On small screens: show user name + role at top */}
            {isSmallScreen() && currentUserDetails?.name && (
              <div className="ccl-profile-menu-user-header">
                <div className="ccl-profile-menu-user-avatar">
                  {(currentUserDetails.name || '').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div className="ccl-profile-menu-user-info">
                  <span className="ccl-profile-menu-user-name">{currentUserDetails.name}</span>
                  <span className="ccl-profile-menu-user-role">{currentUserDetails.role}</span>
                </div>
              </div>
            )}
            {/* Actions */}
            <button
              className="ccl-profile-menu-item"
              onClick={() => { setChangePass(true); handleClose(); }}
            >
              <LockIcon className="ccl-profile-menu-icon" />
              <span>Change Password</span>
            </button>
            <button
              className="ccl-profile-menu-item ccl-profile-menu-item--danger"
              onClick={() => getLogout()}
            >
              <ExitToApp className="ccl-profile-menu-icon" />
              <span>Logout</span>
            </button>
          </div>
        </Popover>

        {/* Page content */}
        <div className="app-content">
          {props.children}
        </div>

      </div>

      {/* Change Password modal */}
      <SimpleModal
        open={openChangePass}
        handleClose={() => setChangePass(false)}
        body={() => <ChangePass setSnack={setSnack} setChangePass={setChangePass} />}
      />

      {/* Auto-logout warning modal */}
      <SimpleModal
        open={showWarningModal}
        handleClose={stayLoggedIn}
        isNew={true}
        isFooter={true}
        size="custom"
        header={logoutStrings.warningHeader}
        body={warningModalBody}
        cancelButtonName={logoutStrings.stayLoggedIn}
        footer={<Button label={logoutStrings.logout} onClick={() => getLogout('autologout')} />}
      />

    </div>
  );
};

export default CustomAppBar;
